import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import BaseMap from '../interfaces/BaseMap';
import IFilterSettings from '../interfaces/IFilterSettings';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import ISavedView, {
    ISavedViewCreateState,
    ISavedViewIncludes,
    ISavedViewUpdateState,
    IUserViewState
} from '../interfaces/ISavedView';
import IVisibleLayers from '../interfaces/IVisibleLayers';
import SavedViewsApiService from '../services/savedViewsApiService';
import DrawMapStore, { DrawMapMode } from './DrawMapStore';
import FilterSettingsStore from './FilterSettingsStore';
import LocationStore from './LocationStore';
import MapStore from './MapStore';

export default class SavedViewStore {
    savedViews: ISavedView[] = [];
    isSavedViewsLoading: ILoadingProps = {};
    previewingSavedView: ISavedView | null = null;
    userViewBackup: IUserViewStateBackup | null = null;

    constructor(
        private savedViewsApiService: SavedViewsApiService,
        private mapStore: MapStore,
        private locationStore: LocationStore,
        private filterSettingsStore: FilterSettingsStore,
        private drawMapStore: DrawMapStore,
    ) {
        makeAutoObservable(this);
    }

    setSavedViews(value: ISavedView[]) {
        this.savedViews = value;
    }

    setIsSavedViewsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isSavedViewsLoading.fetching = value,
            creating: (value: boolean) => this.isSavedViewsLoading.creating = value,
            updating: (value: boolean) => this.isSavedViewsLoading.updating = value,
            deleting: (value: boolean) => this.isSavedViewsLoading.deleting = value,
        };
    }

    initialize() {
        this.setIsSavedViewsLoading().fetching(true);
        this.savedViewsApiService.getSavedViews().then(savedViews => {
            this.setSavedViews(savedViews);
        }).finally(() => {
            this.setIsSavedViewsLoading().fetching(false);
        });
    }

    async createSavedView(savedViewState: ISavedViewCreateState): Promise<number> {
        this.setIsSavedViewsLoading().creating(true);
        try {
            if (this.savedViews.find(v => v.name === savedViewState.name)) {
                const errorMessage = 'A saved view with this name already exists.';
                toast.error(errorMessage);
                throw new Error(errorMessage);
            }

            const viewStateJson = this.buildViewStateJson(savedViewState);
            const createdSavedView = await this.savedViewsApiService.createSavedView({
                name: savedViewState.name,
                isShared: savedViewState.isShared,
                viewStateJson,
            });

            this.setSavedViews([
                ...this.savedViews,
                createdSavedView,
            ]);

            return createdSavedView.id;
        } finally {
            this.setIsSavedViewsLoading().creating(false);
        }
    }

    async updateSavedView(savedViewState: ISavedViewUpdateState): Promise<void> {
        this.setIsSavedViewsLoading().updating(true);
        try {
            const viewStateJson = this.buildViewStateJson(savedViewState);
            const updatedSavedView = await this.savedViewsApiService.updateSavedView({
                id: savedViewState.id,
                name: savedViewState.name,
                isShared: savedViewState.isShared,
                viewStateJson,
            });

            const existing = this.savedViews.find(v => v.id === savedViewState.id);
            if (existing) {
                existing.name = updatedSavedView.name;
                existing.isShared = updatedSavedView.isShared;
                existing.viewState = updatedSavedView.viewState;
                existing.modifiedDate = updatedSavedView.modifiedDate;
                existing.createdByUser = updatedSavedView.createdByUser;
                existing.modifiedByUser = updatedSavedView.modifiedByUser;

                this.setSavedViews([
                    ...this.savedViews.filter(v => v.id !== existing.id),
                    existing,
                ]);
            } else {
                this.setSavedViews([
                    ...this.savedViews.filter(v => v.id !== updatedSavedView.id),
                    updatedSavedView,
                ]);
            }
        } finally {
            this.setIsSavedViewsLoading().updating(false);
        }
    }

    async deleteSavedView(id: number) {
        this.setIsSavedViewsLoading().deleting(true);
        try {
            await this.savedViewsApiService.deleteSavedView(id);

            this.setSavedViews([
                ...this.savedViews.filter(v => v.id !== id),
            ]);
        } finally {
            this.setIsSavedViewsLoading().deleting(false);
        }
    }

    startSavedViewPreview(id: number) {
        this.captureUserViewBackupIfNeeded();
        this.setPreviewingSavedView(id);
    }

    applySavedViewPreview() {
        this.setPreviewingSavedView(null);
        this.setUserViewBackup(null);
    }

    discardSavedViewPreview() {
        this.restoreUserViewBackup();
        this.setPreviewingSavedView(null);
        this.setUserViewBackup(null);
    }

    private setPreviewingSavedView = _.debounce((id: number | null) => {
        this.previewingSavedView = this.savedViews.find(sv => sv.id === id) || null;
        if (this.previewingSavedView)
            this.applyViewState(this.previewingSavedView.viewState);
    }, 500, { leading: true, trailing: true })

    private setUserViewBackup(value: IUserViewStateBackup | null) {
        this.userViewBackup = value;
    }

    private buildViewStateJson(includes: ISavedViewIncludes): string {
        const snapshot: IUserViewState = {};

        if (includes.includeFocusArea) {
            if (this.drawMapStore.mode === DrawMapMode.FocusArea && this.drawMapStore.result) {
                snapshot.focusArea = _.cloneDeep(this.drawMapStore.result);
            }
        }

        if (includes.includeAllFilterSettings) {
            snapshot.filters = this.filterSettingsStore.filters;
        }

        if (includes.includeVisibleLayerSettings) {
            snapshot.visibleLayers = this.mapStore.visibleLayers;
        }

        if (includes.includeBaseMapSettings) {
            snapshot.baseMap = this.mapStore.baseMap;
        }

        if (includes.includeCompanyIdsToViewOnMap) {
            snapshot.companyIdsToViewOnMap = [...this.locationStore.checkedCompanyIds];
        }

        return JSON.stringify(snapshot);
    }

    private applyViewState(viewState: IUserViewState) {
        if (viewState.focusArea) {
            this.drawMapStore.drawFocusArea(viewState.focusArea);
        } else {
            this.drawMapStore.clear();
        }

        if (viewState.filters) {
            this.filterSettingsStore.setFilters(viewState.filters);
        }

        if (viewState.visibleLayers) {
            this.mapStore.setVisibleLayers(viewState.visibleLayers);
        }

        if (viewState.baseMap) {
            this.mapStore.setBaseMap(viewState.baseMap);
        }

        if (viewState.companyIdsToViewOnMap) {
            this.locationStore.setCheckedCompanyIds(viewState.companyIdsToViewOnMap);
        }
    }

    private captureUserViewBackupIfNeeded() {
        if (this.userViewBackup) {
            return;
        }

        this.setUserViewBackup({
            focusArea: (this.drawMapStore.mode === DrawMapMode.FocusArea && this.drawMapStore.result) ? _.cloneDeep(this.drawMapStore.result) : undefined,
            filters: this.filterSettingsStore.filters,
            visibleLayers: this.mapStore.visibleLayers,
            baseMap: this.mapStore.baseMap,
            companyIdsToViewOnMap: [...this.locationStore.checkedCompanyIds],
        });
    }

    private restoreUserViewBackup() {
        if (!this.userViewBackup) {
            return;
        }

        this.userViewBackup.focusArea ? this.drawMapStore.drawFocusArea(this.userViewBackup.focusArea) : this.drawMapStore.clear();
        this.filterSettingsStore.setFilters(this.userViewBackup.filters);
        this.mapStore.setVisibleLayers(this.userViewBackup.visibleLayers);
        this.mapStore.setBaseMap(this.userViewBackup.baseMap);
        this.locationStore.setCheckedCompanyIds(this.userViewBackup.companyIdsToViewOnMap);

        this.setUserViewBackup(null);
    }

    clear() {
        this.setSavedViews([]);
        this.isSavedViewsLoading = {};
        this.setPreviewingSavedView(null);
    }
}

interface IUserViewStateBackup {
    focusArea?: FeatureCollection<Geometry, GeoJsonProperties>;
    filters: IFilterSettings;
    visibleLayers: IVisibleLayers;
    baseMap: BaseMap;
    companyIdsToViewOnMap: number[];
}